import React, { useEffect, useMemo, useState } from 'react';
import { PageSubheader, TicketDetailModal, CreateTicketModal } from '../shared';
import { InventoryIssuesTable } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { InventoryIssueRow } from './callCenterInventoryIssuesTypes';
import { useTicketsStore } from '../../../store/ticketsStore';
import { uiPriority, uiStatus } from '../shared/ticketAdapter';
import { Ticket } from '../../../services/ticketsApi';
import styles from './CallCenterInventoryIssues.module.css';

function adapt(t: Ticket): InventoryIssueRow {
  return {
    id: String(t.id),
    ticketId: `#${t.ticket_number}`,
    sku: '—',
    warehouse: '—',
    issueType: t.subject,
    priority: uiPriority(t.priority),
    status: uiStatus(t.status),
  };
}

const CallCenterInventoryIssues: React.FC = () => {
  const items = useTicketsStore((s) => s.items);
  const loading = useTicketsStore((s) => s.loading);
  const error = useTicketsStore((s) => s.error);
  const setFilters = useTicketsStore((s) => s.setFilters);
  const fetchList = useTicketsStore((s) => s.fetchList);

  useEffect(() => {
    setFilters({ category: 'inventory' });
    fetchList();
  }, [setFilters, fetchList]);

  const rows = useMemo<InventoryIssueRow[]>(() => items.map(adapt), [items]);
  const [openTicketId, setOpenTicketId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className={styles.container}>
      <PageSubheader
        description="Stock complaints, batch problems, adjustment disputes"
        onAction={() => setShowCreate(true)}
      />
      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && rows.length === 0}
        emptyMessage="No inventory issues to display"
        onRetry={() => fetchList()}
      >
        <InventoryIssuesTable rows={rows} onView={(id) => setOpenTicketId(parseInt(id, 10))} />
      </AsyncBoundary>
      <TicketDetailModal ticketId={openTicketId} onClose={() => setOpenTicketId(null)} />
      <CreateTicketModal
        open={showCreate}
        defaultCategory="inventory"
        lockCategory
        onClose={() => setShowCreate(false)}
        onCreated={(id) => { setOpenTicketId(id); fetchList(); }}
      />
    </div>
  );
};

export const CallCenterInventoryIssuesPage: React.FC = () => <CallCenterInventoryIssues />;

export { CallCenterInventoryIssues };
export default CallCenterInventoryIssues;
